export interface EndpointStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

interface PersistedEndpoint {
    User?: {
        UserId?: string;
        UserAttributes?: Record<string, string[]>;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export interface PinpointStreamEndpoint {
    user?: {
        userid?: string;
        userattributes?: {
            userid?: string[];
            email?: string[];
            name?: string[];
            platform?: string[];
        };
    };
    metrics: Record<string, string>;
}

const browserStorage: EndpointStorage = {
    getItem: (key) => localStorage.getItem(key),
    setItem: (key, value) => localStorage.setItem(key, value),
    removeItem: (key) => localStorage.removeItem(key),
};

export function createEndpointState(storage: EndpointStorage = browserStorage) {
    function keyFor(anonId: string): string {
        return `__endpoint.${anonId}`;
    }

    function read(anonId: string): PersistedEndpoint {
        try {
            const value: unknown = JSON.parse(storage.getItem(keyFor(anonId)) ?? "null");
            return isRecord(value) ? value : {};
        } catch {
            return {};
        }
    }

    return {
        identify(this: void, anonId: string, userId: string, traits: Record<string, unknown>): void {
            const endpoint = read(anonId);
            const existingUser = isRecord(endpoint.User) ? endpoint.User : {};
            const existingAttributes = isRecord(existingUser.UserAttributes) ? existingUser.UserAttributes : {};
            const attributes: Record<string, unknown> = {...existingAttributes};

            for (const [name, value] of Object.entries(traits)) {
                if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
                    attributes[name] = [String(value).slice(0, 200)];
                }
            }

            storage.setItem(
                keyFor(anonId),
                JSON.stringify({
                    ...endpoint,
                    User: {...existingUser, UserId: userId, UserAttributes: attributes},
                }),
            );
        },

        getEndpoint(this: void, anonId: string): PinpointStreamEndpoint {
            const persistedUser = read(anonId).User;
            if (!isRecord(persistedUser)) {
                return {metrics: {}};
            }

            const user: NonNullable<PinpointStreamEndpoint["user"]> = {};
            if (typeof persistedUser.UserId === "string") {
                user.userid = persistedUser.UserId;
            }

            if (isRecord(persistedUser.UserAttributes)) {
                const persistedAttributes = persistedUser.UserAttributes;
                const attributes: NonNullable<NonNullable<PinpointStreamEndpoint["user"]>["userattributes"]> = {};
                const userId = stringArray(persistedAttributes.userId);
                const email = stringArray(persistedAttributes.email);
                const platform = stringArray(persistedAttributes.platform);
                const name = fullName(
                    stringArray(persistedAttributes.firstName),
                    stringArray(persistedAttributes.lastName),
                );

                if (userId) {
                    attributes.userid = userId;
                }
                if (email) {
                    attributes.email = email;
                }
                if (name) {
                    attributes.name = name;
                }
                if (platform) {
                    attributes.platform = platform;
                }
                if (Object.keys(attributes).length > 0) {
                    user.userattributes = attributes;
                }
            }

            return Object.keys(user).length > 0 ? {user, metrics: {}} : {metrics: {}};
        },

        reset(this: void, anonId: string): void {
            storage.removeItem(keyFor(anonId));
        },
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringArray(value: unknown): string[] | undefined {
    return Array.isArray(value) && value.every((item) => typeof item === "string") ? value : undefined;
}

function fullName(firstName: string[] | undefined, lastName: string[] | undefined): string[] | undefined {
    const name = [firstName?.[0], lastName?.[0]].filter((part): part is string => Boolean(part?.trim())).join(" ");
    return name ? [name] : undefined;
}

export const {identify, getEndpoint, reset} = createEndpointState();
